import getActivities from '@/api/library/getActivities.endPoint';
import getTaskTemplate from '@/api/taskTemplates/getTaskTemplate.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import {
  createActivityTypeConf,
  getActivityConfDefaults,
} from '@/modules/activities/ActivityTypesConf';
import expandTaskMiniactivities from '@/modules/activities/expandMiniactivies';
import filterNotAssignableActivities from '@/modules/activities/filterNotAssignableActivities';
import { reorder } from '@/modules/arrayUtils';
import { useEffect } from 'react';

export default function useTaskActivitiesEditor({
  taskId,
  themeId,
  taskTemplateId,
  taskActivities,
  setTaskActivities,
}) {
  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,

      themeTaskActivities: null,
      templateTaskActivities: null,

      allTaskActivities: [],
      availableTaskActivities: [],
      selectedTaskActivities: [],
      activityTypeConfById: null,
    },
    actions: {
      loadTaskTemplate: async () => {
        actions.setState({
          loading: true,
        });
        const taskTemplate = await getTaskTemplate(taskTemplateId);
        const expandedTaskTemplateActivities = expandTaskMiniactivities(
          taskTemplate.taskTemplateActivities
        );
        if (taskId) {
          actions.setState({
            templateTaskActivities: expandedTaskTemplateActivities,
          });
        } else {
          setTaskActivities(expandedTaskTemplateActivities);
          actions.setState({
            templateTaskActivities: expandedTaskTemplateActivities,
            selectedTaskActivities: expandedTaskTemplateActivities,
          });
        }
      },
      onTaskActivityAdded: (addedTaskActivity) => {
        const newTaskActivities = [...state.selectedTaskActivities];
        newTaskActivities.push(addedTaskActivity);
        setTaskActivities(newTaskActivities);
      },
      onTaskActivityRemoved: (taskActivityIndex) => {
        const newTaskActivities = state.selectedTaskActivities.filter(
          (_, index) => index != taskActivityIndex
        );

        setTaskActivities(newTaskActivities);
      },
      loadThemeActivities: async () => {
        const activities = await getActivities({
          themeId,
        });

        const activityTypeConfById = {};

        const themeTaskActivities = filterNotAssignableActivities(
          activities
        ).map((activity) => {
          const activityTypeConf = createActivityTypeConf(activity);
          activityTypeConfById[activity.id] = activityTypeConf;

          const activityConfDefaults =
            activityTypeConf && getActivityConfDefaults(activityTypeConf);

          return {
            activity,
            activityId: activity.id,
            activityConf: activityConfDefaults,
          };
        });

        actions.setState({
          themeTaskActivities: expandTaskMiniactivities(themeTaskActivities),
          activityTypeConfById,
          loading: false,
        });
      },
      setTaskActivityConfOption: (changedTaskActivityIndex, option, value) => {
        const newTaskActivities = state.selectedTaskActivities.map(
          (taskActivity, taskActivityIndex) => {
            if (taskActivityIndex == changedTaskActivityIndex) {
              if (taskActivity.activityConf.miniactivityType) {
                taskActivity.activityConf.options[option] = validateOptionValue(
                  option,
                  value
                );
              } else if (option == 'themes') {
                taskActivity.activityConf.themes = value;
              } else {
                taskActivity.activityConf.options[option] = validateOptionValue(
                  option,
                  value
                );
              }
            }
            return taskActivity;
          }
        );
        setTaskActivities(newTaskActivities);
      },
      onDragEnd: (result) => {
        if (!result.destination) {
          return;
        }

        const selectedTaskActivities = reorder(
          state.selectedTaskActivities,
          result.source.index,
          result.destination.index
        );

        setTaskActivities(selectedTaskActivities);
      },
      calculateAllTaskActivities: () => {
        if (state.themeTaskActivities && state.templateTaskActivities) {
          const activityTypeConfById = { ...state.activityTypeConfById };
          const allTaskActivities = [...state.themeTaskActivities];
          for (const templateTaskActivity of state.templateTaskActivities) {
            const sameTaskActivity = allTaskActivities.find(
              (taskActivity) =>
                taskActivity.activityId == templateTaskActivity.activityId &&
                (taskActivity.activityConf.miniactivityType ==
                  templateTaskActivity.activityConf.miniactivityType ||
                  (taskActivity.activityConf.default &&
                    !templateTaskActivity.activityConf.miniactivityType))
            );

            if (!sameTaskActivity) {
              const activityTypeConf = createActivityTypeConf(
                templateTaskActivity.activity
              );
              activityTypeConfById[templateTaskActivity.activityId] =
                activityTypeConf;
              allTaskActivities.push(templateTaskActivity);
            }
          }
          actions.setState({
            allTaskActivities,
            activityTypeConfById,
          });
        }
      },
      calculateAvailableTaskActivities: () => {
        if (state.allTaskActivities?.length && state.selectedTaskActivities) {
          const availableTaskActivities = state.allTaskActivities.filter(
            (taskActivity) => {
              const sameTaskActivities = state.selectedTaskActivities.filter(
                (selectedTaskActivity) =>
                  selectedTaskActivity.activityId == taskActivity.activityId &&
                  (selectedTaskActivity.activityConf.miniactivityType ==
                    taskActivity.activityConf.miniactivityType ||
                    (!selectedTaskActivity.activityConf.miniactivityType &&
                      taskActivity.activityConf.default))
              );

              let typeConf =
                state.activityTypeConfById[taskActivity.activityId];

              if (typeConf.miniactivities) {
                typeConf = typeConf.miniactivities.find(
                  (miniactivity) =>
                    miniactivity.miniactivityType ==
                      taskActivity.activityConf.miniactivityType ||
                    (!taskActivity.activityConf.miniactivityType &&
                      miniactivity.default)
                );
              }

              if (sameTaskActivities.length == 0) {
                return true;
              }

              if (
                sameTaskActivities.length <
                typeConf.options.allowedLevels?.length
              ) {
                const notUsedLevel = typeConf.options.allowedLevels.find(
                  (level) =>
                    !sameTaskActivities.some(
                      (sameTaskActivity) =>
                        sameTaskActivity.activityConf.options.level == level
                    )
                );

                taskActivity.activityConf = {
                  ...taskActivity.activityConf,
                  options: {
                    ...taskActivity.activityConf.options,
                    level: notUsedLevel,
                  },
                };
                return true;
              }

              return false;
            }
          );
          actions.setState({ availableTaskActivities, loading: false });
        }
      },
    },
  });

  useEffect(() => {
    if (themeId) {
      actions.loadThemeActivities();
    }
  }, [themeId]);

  useEffect(() => {
    if (taskTemplateId) {
      actions.loadTaskTemplate();
    }
  }, [taskTemplateId, taskId]);

  useEffect(() => {
    actions.calculateAllTaskActivities();
  }, [state.themeTaskActivities, state.templateTaskActivities]);

  useEffect(() => {
    actions.calculateAvailableTaskActivities();
  }, [state.allTaskActivities, state.selectedTaskActivities]);

  useEffect(() => {
    if (taskActivities) {
      actions.setState({
        selectedTaskActivities: expandTaskMiniactivities(taskActivities, false),
      });
    }
  }, [taskActivities]);

  return { state, actions };
}

function validateOptionValue(option, value) {
  return {
    repetitions: (value) => Math.max(1, Math.min(5, value)),
    level: (value) => value,
    mode: (value) => value,
    questionsPerScreen: (value) => Math.max(2, Math.min(4, value)),
  }[option](value);
}
